import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const AppContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const AppProvider = ({ children }) => {
  // Auth state
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
  
  // View state
  const [view, setView] = useState('search');
  const [selectedProvider, setSelectedProvider] = useState(null);
  
  // Providers state
  const [providers, setProviders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Lookup data
  const [lookupData, setLookupData] = useState({
    providerTypes: [],
    services: [],
    insuranceProviders: [],
    languages: [],
    certifications: []
  });
  
  // Search/filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ 
    providerTypes: [],
    services: [],
    insurance: [],
    priceMax: 200, 
    rating: 0, 
    availability: [] 
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  
  // Booking state
  const [bookingData, setBookingData] = useState({ 
    date: null, 
    time: null, 
    duration: 2, 
    notes: '', 
    address: { line1: '', city: 'Austin', state: 'TX', zip: '' } 
  });
  const [paymentData, setPaymentData] = useState({ cardNumber: '', expiry: '', cvv: '', name: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  
  // Refs for debouncing
  const searchTimeoutRef = useRef(null);
  const searchParamsRef = useRef({ searchQuery: '', locationQuery: '', filters, page: 1 });

  // API helper function
  const apiCall = useCallback(async (endpoint, options = {}) => {
    const token = localStorage.getItem('accessToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    };
    
    const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
    
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'An error occurred' }));
      throw new Error(err.error || err.message || 'An error occurred');
    }
    
    return response.json();
  }, []);

  // Fetch lookup data
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const data = await apiCall('/lookups/all');
        setLookupData(data);
      } catch (err) {
        console.error('Failed to fetch lookups:', err);
      }
    };
    fetchLookups();
  }, [apiCall]);

  // Core search function
  const executeSearch = useCallback(async () => {
    const { searchQuery: q, locationQuery: loc, filters: f, page } = searchParamsRef.current;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      
      if (q) params.append('q', q);
      if (loc) params.append('location', loc);
      if (f.providerTypes.length) params.append('providerTypes', f.providerTypes.join(','));
      if (f.services.length) params.append('services', f.services.join(','));
      if (f.insurance.length) params.append('insurance', f.insurance.join(','));
      if (f.priceMax < 200) params.append('maxPrice', f.priceMax);
      if (f.rating > 0) params.append('minRating', f.rating);
      if (f.availability.length) params.append('availability', f.availability.join(','));
      
      params.append('page', page);
      params.append('limit', pagination.limit);
      
      const data = await apiCall(`/providers/search?${params.toString()}`);
      
      setProviders(data.providers || []);
      setPagination(prev => ({ 
        ...prev, 
        total: data.pagination?.total || data.total || 0,
        totalPages: data.pagination?.totalPages || 1
      }));
    } catch (err) {
      console.error('Failed to fetch providers:', err);
      setError('Failed to load providers. Please try again.');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall, pagination.limit]);

  // Initial search on mount
  useEffect(() => {
    executeSearch();
    // eslint-disable-next-line
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Search handlers - stable references
  const handleSearchQueryChange = useCallback((value) => {
    setSearchQuery(value);
    searchParamsRef.current.searchQuery = value;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      executeSearch();
    }, 800);
  }, [executeSearch]);

  const handleLocationQueryChange = useCallback((value) => {
    setLocationQuery(value);
    searchParamsRef.current.locationQuery = value;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      executeSearch();
    }, 800);
  }, [executeSearch]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
    searchParamsRef.current.filters = newFilters;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    executeSearch();
  }, [executeSearch]);

  // Auth handlers
  const handleLogin = useCallback(async (email, password) => {
    try {
      const data = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });
      
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      setAccessToken(data.accessToken);
      setUser(data.user);
      setShowAuthModal(false);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [apiCall]);

  const handleRegister = useCallback(async (firstName, lastName, email, password) => {
    try {
      const data = await apiCall('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ firstName, lastName, email, password, role: 'patient' })
      });
      
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      setAccessToken(data.accessToken);
      setUser(data.user);
      setShowAuthModal(false);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [apiCall]);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setAccessToken(null);
    setUser(null);
  }, []);

  // Check if user is logged in
  useEffect(() => {
    const checkAuth = async () => {
      if (accessToken) {
        try {
          const data = await apiCall('/auth/me');
          setUser(data);
        } catch (err) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          setAccessToken(null);
          setUser(null);
        }
      }
    };
    checkAuth();
  }, [accessToken, apiCall]);

  // Booking handlers
  const toggleFavorite = useCallback((id) => {
    setFavorites(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
  }, []);

  const processPayment = useCallback(async () => {
    setIsProcessing(true);
    await new Promise(r => setTimeout(r, 2000));
    setConfirmedBooking({ bookingNumber: 'CHC' + Date.now().toString().slice(-8) });
    setIsProcessing(false);
    setView('confirmation');
  }, []);

  const resetBooking = useCallback(() => {
    setView('search');
    setSelectedProvider(null);
    setBookingData({ date: null, time: null, duration: 2, notes: '', address: { line1: '', city: 'Austin', state: 'TX', zip: '' } });
    setPaymentData({ cardNumber: '', expiry: '', cvv: '', name: '' });
    setConfirmedBooking(null);
  }, []);

  // Utility functions
  const getDaysInMonth = useCallback((date) => {
    const year = date.getFullYear(), month = date.getMonth();
    const firstDay = new Date(year, month, 1), lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  }, []);

  const isDateAvailable = useCallback((date) => {
    if (!date || !selectedProvider) return false;
    const today = new Date(); today.setHours(0,0,0,0);
    const availableDays = selectedProvider.availableDays || selectedProvider.availability || [];
    return date >= today && availableDays.includes(date.getDay());
  }, [selectedProvider]);

  const formatDate = useCallback((d) => {
    return d ? d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';
  }, []);

  const calculateTotal = useCallback(() => {
    return selectedProvider ? ((selectedProvider.hourlyRate || 0) * bookingData.duration * 1.05).toFixed(2) : 0;
  }, [selectedProvider, bookingData.duration]);

  const value = {
    // State
    user,
    view,
    setView,
    selectedProvider,
    setSelectedProvider,
    providers,
    favorites,
    loading,
    error,
    lookupData,
    searchQuery,
    locationQuery,
    showFilters,
    setShowFilters,
    filters,
    pagination,
    bookingData,
    setBookingData,
    paymentData,
    setPaymentData,
    isProcessing,
    confirmedBooking,
    currentMonth,
    setCurrentMonth,
    showAuthModal,
    setShowAuthModal,
    authMode,
    setAuthMode,
    
    // Handlers
    handleSearchQueryChange,
    handleLocationQueryChange,
    handleFilterChange,
    handleLogin,
    handleRegister,
    handleLogout,
    toggleFavorite,
    processPayment,
    resetBooking,
    
    // Utilities
    getDaysInMonth,
    isDateAvailable,
    formatDate,
    calculateTotal,
    apiCall,
    executeSearch
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
